import React from 'react';
import { connect } from 'react-redux';
import { useIntl } from 'react-intl';
import { LocalizedMessage, standardDateNoSecondsOptions } from 'intl/LocalizedComponents';
import _isEmpty from 'lodash/isEmpty';
import _size from 'lodash/size';
import _find from 'lodash/find';
import _map from 'lodash/map';
import _toString from 'lodash/toString';
import _get from 'lodash/get';
import PropTypes from 'prop-types';
// Core components
import Box from 'components/core/Box';
import MenuButton from 'components/core/MenuButton';
// Permissions
import OnlyWith from 'components/permissions/OnlyWith';
// Models
import mAccessWay from 'models/accessWay';
import { getFromScenarioTriggerId } from 'models/scenario';
import { sortElements } from 'models/right';
// Custom
import {
  customerAccessWayDataShape, customerAccessWayRightDataShape, menuItemsShape, deviceDataShape, scenarioDataShape, planningDataShape, permissionsShape,
} from 'constants/PropTypesShapes';
import { getResourcePeriodDesc } from 'utils/Tools';
import { dateGenerator } from 'utils/DateUtils';
import InformationItem from 'components/items/InformationItem';
import ScenarioTriggerLabel from 'installations/assets/scenarios/ScenarioTriggerLabel';
import CustomerRightTriggerButton from './CustomerRightTriggerButton';

const accessWayRight = (intl, right, timezone, devices, scenarios, plannings, rightButton) => {
  // The scenario
  const scenario = getFromScenarioTriggerId(scenarios, right.scenario_trigger_id);
  // The scenario trigger
  const scenarioTrigger = _find(scenario.scenario_triggers, ['id', right.scenario_trigger_id]);
  // The concerned device
  const device = _find(devices, ['id', scenario.device_id]);

  // Set period desc
  const periodDesc = getResourcePeriodDesc(right);

  const { begin, end, planning_id: planningId } = right;

  // Set begin / end desc
  let beginDesc;
  let endDesc;
  if (begin) {
    beginDesc = intl.formatDate(dateGenerator.dateFromTimezone(begin, timezone), standardDateNoSecondsOptions);
  }
  if (end) {
    endDesc = intl.formatDate(dateGenerator.dateFromTimezone(end, timezone), standardDateNoSecondsOptions);
  }

  // Set planning
  const planning = _find(plannings, ['id', planningId]);

  // Define if right has restrictions
  const hasRestrictions = begin || end || !!planning;

  return {
    key: _toString(right.id),
    icon: hasRestrictions ? 'itemRightRestrictions' : 'itemRightNoRestrictions',
    label: (
      <div>
        <LocalizedMessage id="info.rightPeriodDesc" values={{ periodDesc, beginDesc, endDesc }} />
        {planning && (
          <React.Fragment>
            <br />
            <LocalizedMessage id="info.subjectToPlanning" values={{ planningName: planning.name }} />
          </React.Fragment>
        )}
      </div>
    ),
    value: (
      <div>
        {scenario.name}
        <br />
        <ScenarioTriggerLabel device={device} scenarioTrigger={scenarioTrigger} />
      </div>
    ),
    rightButton,
  };
};

const RightAction = ({ right, desc }) => {
  const { type, cb } = desc;
  if (type === 'button_trigger') {
    return (
      <CustomerRightTriggerButton id={right.id} onTrigger={() => cb(right)} />
    );
  }
  return null;
};

RightAction.propTypes = {
  right: customerAccessWayRightDataShape.isRequired,
  desc: PropTypes.shape({
    type: PropTypes.string.isRequired,
    cb: PropTypes.func.isRequired,
  }).isRequired,
};

const CustomerAccessWayItem = ({
  accessWay, timezone, countryCode, devices, scenarios, plannings, menuItems, active, permissions, displayRights, rightAction, initiallyOpen,
}) => {
  const intl = useIntl();

  return (
    <Box type="highlightable" highlighted={active}>
      <InformationItem
        label={
          accessWay.enabled ? intl.formatMessage({ id: 'info.rightsQuantity' }, { quantity: mAccessWay.rightsQuantity(accessWay) })
            : <div className="text-warning mt-1">{intl.formatMessage({ id: 'info.disabled' }, { gender: 'male' })}</div>
        }
        value={mAccessWay.desc(accessWay, countryCode)}
        icon={mAccessWay.icon(accessWay)}
        rightButton={!_isEmpty(menuItems) ? <OnlyWith permissions={permissions}><MenuButton obj={accessWay} items={menuItems} /></OnlyWith> : null}
        initiallyOpen={initiallyOpen}
        nestedItems={
          (displayRights && _size(accessWay.rights) > 0)
            ? _map(sortElements(accessWay.rights), (right) => (
              accessWayRight(
                intl,
                right,
                timezone,
                devices,
                scenarios,
                plannings,
                rightAction ? <OnlyWith permissions={rightAction.permissions}><RightAction right={right} desc={rightAction.desc} /></OnlyWith> : null,
              )
            ))
            : null
        }
      />
    </Box>
  );
};

CustomerAccessWayItem.propTypes = {
  accessWay: customerAccessWayDataShape.isRequired,
  timezone: PropTypes.string.isRequired,
  countryCode: PropTypes.string.isRequired,
  devices: PropTypes.arrayOf(deviceDataShape).isRequired,
  scenarios: PropTypes.arrayOf(scenarioDataShape),
  plannings: PropTypes.arrayOf(planningDataShape),
  menuItems: menuItemsShape,
  active: PropTypes.bool,
  permissions: permissionsShape,
  displayRights: PropTypes.bool,
  rightAction: PropTypes.shape({
    permissions: PropTypes.string,
    desc: PropTypes.shape({
      type: PropTypes.string.isRequired,
    }).isRequired,
  }),
  initiallyOpen: PropTypes.bool,
};

CustomerAccessWayItem.defaultProps = {
  scenarios: null,
  plannings: null,
  active: false,
  menuItems: null,
  permissions: null,
  displayRights: false,
  rightAction: null,
  initiallyOpen: false,
};

const mapStateToProps = (state) => ({
  timezone: _get(state.installation.current, 'data.timezone'),
  countryCode: state.intl.countryCode,
  devices: state.deviceData.elements,
  scenarios: state.installationScenario.list.elements,
  plannings: state.installationPlanning.list.elements,
});

module.exports = connect(
  mapStateToProps,
  null,
)(CustomerAccessWayItem);



// WEBPACK FOOTER //
// ./routes/app/routes/installations/assets/customers/CustomerAccessWayItem.js